import React from "react";
import { Divider } from "@material-ui/core";
import { useEffect } from "react";
import { useTranslate } from "react-admin";
import { useFormContext, useWatch, Validate } from "react-hook-form";
import { ApproveOrDenyTenantRegistrationDto } from "../../../common/data/service-proxies";
import { useAppData } from "../../../common/hooks/useAppData";
import { HookFormSimpleForm } from "../../common/form/hook-form/HookFormSimpleForm";
import { HookFormBooleanInput } from "../../common/form/hook-form/inputs/HookFormBooleanInput";
import { HookFormSelectInput } from "../../common/form/hook-form/inputs/HookFormSelectInput";
import { HookFormTextInput } from "../../common/form/hook-form/inputs/HookFormTextInput";
import { v4 as uuidv4 } from "uuid";
import _isString from "lodash/isString";
import { HookFormTenantRegistrationPicker } from "../../common/form/hook-form/inputs/HookFormTenantRegistrationPicker";

interface Props {}

const isString = (value: any): value is string => {
  return _isString(value);
};

const getDatabaseName = (tenantName?: string) => {
  return `${tenantName}_${uuidv4()}`.replace(/\W+/g, "").toLocaleLowerCase();
};

const validateEmail: Validate<any> = (value) => {
  if (value === null) return true;
  if (!isString(value)) return "Not a string";
  let flag: boolean = false;
  for (let index = 0; index < value.length; index++) {
    if (value[index] === "@") {
      if (flag || index === 0 || index === value.length - 1)
        return "Must be a valid email address";
      flag = true;
    }
  }

  return !flag ? "Must be a valid email address" : undefined;
};

export const ApproveRegistrationForm = (props: Props) => {
  const translate = useTranslate();
  const databases = useAppData((x) => x.databaseServers) ?? [];
  const databaseOptions = databases.map((x) => ({ id: x, name: x }));
  const apiUrl = useAppData((x) => x.apiUrl);
  const webAppUrl = useAppData((x) => x.webAppUrl);
  const { control, setValue, getValues } = useFormContext<
    ApproveOrDenyTenantRegistrationDto
  >();

  const record = React.useMemo(() => getValues(), [getValues]);
  const selectedTenantName = useWatch({
    control,
    name: `tenantInfo.name`,
    defaultValue: record?.tenantInfo?.name ?? "",
  });

  const parentTenantId = useWatch({
    control,
    name: "tenantInfo.parentTenantId",
    defaultValue: record?.tenantInfo?.parentTenantId ?? "",
  });

  useEffect(() => {
    setValue(
      "tenantInfo.dbName",
      getDatabaseName(
        selectedTenantName.length ? selectedTenantName : record.tenantInfo?.name
      ),
      {
        shouldValidate: true,
        shouldDirty: true,
      }
    );
  }, [selectedTenantName, setValue, record]);

  useEffect(() => {
    if (!!parentTenantId) {
      setValue("tenantInfo.hasMasterAccount", false, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
  }, [parentTenantId, setValue]);

  return (
    <HookFormSimpleForm {...props}>
      <HookFormTextInput<ApproveOrDenyTenantRegistrationDto>
        {...props}
        control={control}
        name="tenantInfo.name"
        label="resources.master-tenants.labels.name"
        rules={{ required: translate("ra.validation.required") }}
        defaultValue={record?.tenantInfo?.name ?? ""}
      />
      <HookFormTextInput<ApproveOrDenyTenantRegistrationDto>
        {...props}
        control={control}
        name="tenantInfo.customerContactName"
        label="resources.master-tenants.labels.customerContactName"
        defaultValue={record?.tenantInfo?.customerContactName ?? ""}
      />
      <HookFormTextInput<ApproveOrDenyTenantRegistrationDto>
        {...props}
        control={control}
        name="tenantInfo.customerContactPhone"
        label="resources.master-tenants.labels.customerContactPhone"
        defaultValue={record?.tenantInfo?.customerContactPhone ?? ""}
      />
      <HookFormTextInput<ApproveOrDenyTenantRegistrationDto>
        {...props}
        control={control}
        name="tenantInfo.customerContactEmail"
        label="resources.master-tenants.labels.customerContactEmail"
        defaultValue={record?.tenantInfo?.customerContactEmail ?? ""}
      />
      <Divider variant="fullWidth" style={{ marginBottom: "20px" }} />
      <HookFormTextInput<ApproveOrDenyTenantRegistrationDto>
        {...props}
        control={control}
        name="tenantInfo.domainName"
        label="resources.master-tenants.labels.domainName"
        rules={{
          required: translate("ra.validation.required"),
          pattern: {
            value: /^([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/,
            message: translate(
              "resources.master-tenants.labels.mustBeValidDomainName"
            ),
          },
        }}
        defaultValue={record?.tenantInfo?.domainName ?? ""}
      />
      <HookFormTextInput<ApproveOrDenyTenantRegistrationDto>
        {...props}
        control={control}
        name="tenantInfo.adminName"
        label="resources.master-tenants.labels.adminName"
        rules={{ required: translate("ra.validation.required") }}
        defaultValue={record?.tenantInfo?.adminName ?? "Administrator"}
      />
      <HookFormTextInput<ApproveOrDenyTenantRegistrationDto>
        {...props}
        control={control}
        name="tenantInfo.adminEmailAddress"
        label="resources.master-tenants.labels.adminEmailAddress"
        rules={{
          required: translate("ra.validation.required"),
          validate: validateEmail,
        }}
        defaultValue={record?.tenantInfo?.adminEmailAddress ?? ""}
      />
      <HookFormBooleanInput<ApproveOrDenyTenantRegistrationDto>
        {...props}
        control={control}
        name="tenantInfo.hasMasterAccount"
        label="resources.master-tenant-registrations.labels.hasMasterAccount"
        defaultValue={record?.tenantInfo?.hasMasterAccount ?? false}
        options={{ disabled: !!parentTenantId }}
      />
      <HookFormBooleanInput<ApproveOrDenyTenantRegistrationDto>
        {...props}
        control={control}
        name="tenantInfo.hasOwnElds"
        label="resources.master-tenants.labels.hasOwnElds"
        defaultValue={record?.tenantInfo?.hasOwnElds ?? false}
      />
      <HookFormTenantRegistrationPicker<ApproveOrDenyTenantRegistrationDto>
        {...props}
        control={control}
        name="tenantInfo.parentTenantId"
        label="resources.master-tenant-registrations.labels.parentTenantId"
        variant="outlined"
        defaultValue={""}
        emptyText="(None)"
      />
      <Divider variant="fullWidth" style={{ marginBottom: "20px" }} />
      <HookFormSelectInput<ApproveOrDenyTenantRegistrationDto>
        {...props}
        control={control}
        choices={[{ id: webAppUrl, name: webAppUrl }]}
        name="tenantInfo.appUrl"
        label="resources.master-tenants.labels.appUrl"
        rules={{ required: translate("ra.validation.required") }}
        defaultValue={record?.tenantInfo?.appUrl ?? ""}
      />
      <HookFormSelectInput<ApproveOrDenyTenantRegistrationDto>
        {...props}
        control={control}
        choices={[{ id: apiUrl, name: apiUrl }]}
        name="tenantInfo.webApiUrl"
        label="resources.master-tenants.labels.webApiUrl"
        rules={{ required: translate("ra.validation.required") }}
        defaultValue={record?.tenantInfo?.webApiUrl ?? ""}
      />
      <HookFormSelectInput<ApproveOrDenyTenantRegistrationDto>
        {...props}
        control={control}
        choices={databaseOptions}
        name="tenantInfo.dbServer"
        label="resources.master-tenants.labels.dbServer"
        rules={{ required: translate("ra.validation.required") }}
        defaultValue={record?.tenantInfo?.dbServer ?? ""}
      />
      <HookFormTextInput<ApproveOrDenyTenantRegistrationDto>
        {...props}
        control={control}
        name="tenantInfo.dbName"
        label="resources.master-tenants.labels.dbName"
        defaultValue={getDatabaseName(record?.tenantInfo?.name)}
        rules={{ required: translate("ra.validation.required") }}
        options={{ disabled: true }}
      />
    </HookFormSimpleForm>
  );
};
